import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useTimezone } from '@/hooks/useTimezone';

type TimezoneDatePickerProps = Omit<DatePickerProps, 'value' | 'onChange'> & {
  value?: Dayjs | string | null;
  onChange?: (value: Dayjs | null, dateString: string | string[]) => void;
};

export function TimezoneDatePicker({
  value,
  onChange,
  ...rest
}: TimezoneDatePickerProps) {
  const { mode, toPickerDisplay, fromPickerDisplay } = useTimezone();

  const displayValue = useMemo(() => {
    if (value == null || value === '') {
      return null;
    }
    if (dayjs.isDayjs(value)) {
      return toPickerDisplay(value.toISOString());
    }
    return toPickerDisplay(value);
  }, [mode, toPickerDisplay, value]);

  return (
    <DatePicker
      {...rest}
      value={displayValue}
      onChange={(nextValue, dateString) => {
        onChange?.(fromPickerDisplay(nextValue), dateString);
      }}
    />
  );
}

type TimezoneRangePickerProps = Omit<
  React.ComponentProps<typeof DatePicker.RangePicker>,
  'value' | 'onChange'
> & {
  value?: Array<Dayjs | string | null> | null;
  onChange?: (
    value: [Dayjs | null, Dayjs | null] | null,
    dateString: [string, string],
  ) => void;
};

export function TimezoneRangePicker({
  value,
  onChange,
  ...rest
}: TimezoneRangePickerProps) {
  const { mode, toPickerDisplay, fromPickerDisplay } = useTimezone();

  const displayValue = useMemo(() => {
    if (!value || !Array.isArray(value)) {
      return null;
    }
    const [start, end] = value;
    const startValue = start
      ? dayjs.isDayjs(start)
        ? toPickerDisplay(start.toISOString())
        : toPickerDisplay(start)
      : null;
    const endValue = end
      ? dayjs.isDayjs(end)
        ? toPickerDisplay(end.toISOString())
        : toPickerDisplay(end)
      : null;
    if (!startValue && !endValue) {
      return null;
    }
    return [startValue, endValue] as [Dayjs | null, Dayjs | null];
  }, [mode, toPickerDisplay, value]);

  return (
    <DatePicker.RangePicker
      {...rest}
      value={displayValue}
      onChange={(nextValue, dateString) => {
        onChange?.(
          nextValue
            ? [
                fromPickerDisplay(nextValue[0]),
                fromPickerDisplay(nextValue[1]),
              ]
            : null,
          dateString,
        );
      }}
    />
  );
}
